import { EnemyAI } from '../sim/types'

/** Boss attack patterns cycled by the boss AI (see updateEnemies). 'nova' is the enrage/phase-2 desperation blast. */
export type BossPattern = 'slam' | 'ring' | 'charge' | 'beam' | 'summon' | 'nova'

export interface EnemyDef {
  id: string
  name: string
  hp: number
  damage: number
  speed: number
  radius: number
  xp: number
  gold: number
  ai: EnemyAI
  color: number
  scale?: number
  /** ranged enemies: projectile speed/damage handled by ai. */
  rangedCooldown?: number
  splitInto?: string
  splitCount?: number
  /** earliest run time (s) this enemy may appear, used by the spawn director. */
  minTime?: number
  /** relative spawn weight once unlocked. */
  weight?: number
  /** bombers: detonate an AoE blast when they die. */
  explodeOnDeath?: { radius: number; damage: number }
  /** summoners: periodically spawn minions. */
  summon?: { id: string; count: number; interval: number }
  /** armored: fraction (0..1) of frontal damage blocked — flank, burn or blow it up. */
  frontArmor?: number
  /** buffers: pulse an empower aura over nearby aliens. */
  buff?: { radius: number; interval: number }
  /** healers: periodically mend nearby wounded allies (bounded, never heals bosses). */
  heal?: { radius: number; interval: number; amount: number }
  /** bosses: ordered list of attack patterns cycled by the boss AI. */
  patterns?: BossPattern[]
  /** bosses: HP fraction (0..1) below which the boss enrages (faster, deadlier). */
  enrageAt?: number
  /** bosses: signature power beyond stats (see updateEnemies boss branch). */
  bossPower?: 'weaponlock' | 'gravity' | 'shield' | 'clone' | 'quake' | 'empower' | 'drain'
  /** hidden bosses are only summoned under special conditions (e.g. high curse), not in pools. */
  hidden?: boolean
}

// Original roster, themed but distinct from the source game.
export const ENEMY_LIST: EnemyDef[] = [
  {
    id: 'grub',
    name: 'Larvoid',
    hp: 10,
    damage: 6,
    speed: 2.6,
    radius: 0.5,
    xp: 2,
    gold: 1,
    ai: 'chaser',
    color: 0x8fce5a,
    minTime: 0,
    weight: 100,
  },
  {
    id: 'runt',
    name: 'Sporelet',
    hp: 7,
    damage: 5,
    speed: 4.1,
    radius: 0.42,
    xp: 2,
    gold: 1,
    ai: 'chaser',
    color: 0xe0d24a,
    scale: 0.85,
    minTime: 25,
    weight: 80,
  },
  {
    id: 'brute',
    name: 'Gravoid Brute',
    hp: 40,
    damage: 12,
    speed: 2.0,
    radius: 0.8,
    xp: 4,
    gold: 3,
    ai: 'chaser',
    color: 0xc0613a,
    scale: 1.4,
    minTime: 45,
    weight: 45,
  },
  {
    id: 'spitter',
    name: 'Acid Spitter',
    hp: 16,
    damage: 8,
    speed: 1.8,
    radius: 0.55,
    xp: 3,
    gold: 2,
    ai: 'ranged',
    color: 0x9b59b6,
    rangedCooldown: 2.2,
    minTime: 60,
    weight: 40,
  },
  {
    id: 'dasher',
    name: 'Zip Drone',
    hp: 22,
    damage: 14,
    speed: 3.2,
    radius: 0.5,
    xp: 4,
    gold: 3,
    ai: 'charger',
    color: 0x46c8d8,
    minTime: 90,
    weight: 38,
  },
  {
    id: 'blob',
    name: 'Ooze Cluster',
    hp: 34,
    damage: 9,
    speed: 1.9,
    radius: 0.7,
    xp: 3,
    gold: 2,
    ai: 'splitter',
    color: 0x5ad0a0,
    splitInto: 'blobling',
    splitCount: 3,
    minTime: 110,
    weight: 30,
  },
  {
    id: 'blobling',
    name: 'Goolet',
    hp: 8,
    damage: 6,
    speed: 3.0,
    radius: 0.4,
    xp: 1,
    gold: 1,
    ai: 'chaser',
    color: 0x7ee0b8,
    scale: 0.7,
    weight: 0, // only spawned via split
  },
  {
    id: 'wisp',
    name: 'Plasma Wisp',
    hp: 18,
    damage: 10,
    speed: 2.4,
    radius: 0.45,
    xp: 3,
    gold: 2,
    ai: 'orbiter',
    color: 0xb18cff,
    minTime: 150,
    weight: 28,
  },
  {
    id: 'reaver',
    name: 'Void Reaver',
    hp: 70,
    damage: 18,
    speed: 2.7,
    radius: 0.85,
    xp: 8,
    gold: 6,
    ai: 'chaser',
    color: 0xd23a6a,
    scale: 1.5,
    minTime: 210,
    weight: 24,
  },
  {
    id: 'sniper',
    name: 'Beam Sniper',
    hp: 30,
    damage: 16,
    speed: 1.4,
    radius: 0.55,
    xp: 6,
    gold: 5,
    ai: 'ranged',
    color: 0xff7a3d,
    rangedCooldown: 3.0,
    minTime: 240,
    weight: 22,
  },
  {
    id: 'swarmling',
    name: 'Swarmite',
    hp: 4,
    damage: 4,
    speed: 4.7,
    radius: 0.34,
    xp: 1,
    gold: 1,
    ai: 'chaser',
    color: 0xe08a4a,
    scale: 0.65,
    minTime: 70,
    weight: 70,
  },
  {
    id: 'archer',
    name: 'Dart Drone',
    hp: 20,
    damage: 12,
    speed: 2.0,
    radius: 0.5,
    xp: 4,
    gold: 3,
    ai: 'ranged',
    color: 0xd0a050,
    rangedCooldown: 1.8,
    minTime: 130,
    weight: 26,
  },
  {
    id: 'bomber',
    name: 'Volatile Pod',
    hp: 26,
    damage: 10,
    speed: 2.6,
    radius: 0.6,
    xp: 5,
    gold: 4,
    ai: 'charger',
    color: 0xff5a2d,
    explodeOnDeath: { radius: 3, damage: 22 },
    minTime: 120,
    weight: 26,
  },
  {
    id: 'ghoul',
    name: 'Husk Walker',
    hp: 30,
    damage: 12,
    speed: 3.0,
    radius: 0.55,
    xp: 4,
    gold: 3,
    ai: 'chaser',
    color: 0x88aa66,
    minTime: 140,
    weight: 30,
  },
  {
    id: 'stalker',
    name: 'Phase Stalker',
    hp: 22,
    damage: 12,
    speed: 3.2,
    radius: 0.5,
    xp: 4,
    gold: 3,
    ai: 'orbiter',
    color: 0x9c6cd0,
    minTime: 170,
    weight: 24,
  },
  {
    id: 'shielder',
    name: 'Bulwark Drone',
    hp: 90,
    damage: 14,
    speed: 1.6,
    radius: 0.85,
    xp: 7,
    gold: 6,
    ai: 'armored',
    frontArmor: 0.55,
    color: 0x6688aa,
    scale: 1.6,
    minTime: 180,
    weight: 22,
  },
  {
    id: 'summoner',
    name: 'Hive Caller',
    hp: 50,
    damage: 8,
    speed: 1.5,
    radius: 0.6,
    xp: 9,
    gold: 8,
    ai: 'ranged',
    color: 0xb060d0,
    rangedCooldown: 2.6,
    summon: { id: 'swarmling', count: 2, interval: 5 },
    minTime: 160,
    weight: 16,
  },
  {
    id: 'crystalback',
    name: 'Crystaloid',
    hp: 44,
    damage: 10,
    speed: 1.8,
    radius: 0.7,
    xp: 5,
    gold: 4,
    ai: 'splitter',
    color: 0x66d0c0,
    splitInto: 'shard',
    splitCount: 4,
    minTime: 200,
    weight: 20,
  },
  {
    id: 'shard',
    name: 'Shardling',
    hp: 6,
    damage: 6,
    speed: 3.4,
    radius: 0.36,
    xp: 1,
    gold: 1,
    ai: 'chaser',
    color: 0x9fe0d4,
    scale: 0.6,
    weight: 0, // split-only
  },
  {
    id: 'revenant',
    name: 'Revenant Wraith',
    hp: 120,
    damage: 22,
    speed: 2.6,
    radius: 0.85,
    xp: 11,
    gold: 9,
    ai: 'chaser',
    color: 0x884466,
    scale: 1.6,
    minTime: 280,
    weight: 18,
  },
  { id: 'scuttle', name: 'Carapod Scuttler', hp: 12, damage: 7, speed: 3.6, radius: 0.45, xp: 2, gold: 2, ai: 'chaser', color: 0xd08050, minTime: 50, weight: 48 },
  { id: 'locust', name: 'Star Locust', hp: 3, damage: 4, speed: 5.0, radius: 0.3, xp: 1, gold: 1, ai: 'chaser', color: 0xd4b83a, scale: 0.6, minTime: 80, weight: 60 },
  { id: 'spittoad', name: 'Toxin Croaker', hp: 18, damage: 9, speed: 1.8, radius: 0.55, xp: 3, gold: 2, ai: 'ranged', rangedCooldown: 2.0, color: 0x9ed13b, minTime: 95, weight: 30 },
  { id: 'imp', name: 'Gravel Slinger', hp: 14, damage: 10, speed: 2.2, radius: 0.45, xp: 3, gold: 2, ai: 'ranged', rangedCooldown: 1.6, color: 0x8fa85a, minTime: 115, weight: 26 },
  { id: 'roomba', name: 'Rogue Sweeper', hp: 16, damage: 8, speed: 2.8, radius: 0.5, xp: 3, gold: 2, ai: 'orbiter', color: 0xc0c0d0, minTime: 120, weight: 26 },
  { id: 'boar', name: 'Rampager', hp: 28, damage: 14, speed: 3.0, radius: 0.6, xp: 4, gold: 3, ai: 'charger', color: 0xc08060, minTime: 130, weight: 28 },
  { id: 'batcloud', name: 'Swarm Cloud', hp: 10, damage: 7, speed: 3.0, radius: 0.4, xp: 2, gold: 1, ai: 'orbiter', color: 0x6a5a8a, minTime: 140, weight: 30 },
  { id: 'magmabrute', name: 'Magma Colossus', hp: 50, damage: 14, speed: 2.2, radius: 0.75, xp: 6, gold: 5, ai: 'chaser', color: 0xc0502e, scale: 1.4, explodeOnDeath: { radius: 3, damage: 24 }, minTime: 175, weight: 22 },
  { id: 'voodoo', name: 'Hex Caster', hp: 46, damage: 9, speed: 1.6, radius: 0.6, xp: 8, gold: 7, ai: 'ranged', rangedCooldown: 2.8, color: 0xb060d0, summon: { id: 'locust', count: 3, interval: 5 }, minTime: 190, weight: 16 },
  { id: 'ironlich', name: 'Iron Sentinel', hp: 80, damage: 16, speed: 1.7, radius: 0.8, xp: 7, gold: 6, ai: 'chaser', color: 0x8fa0b5, scale: 1.5, minTime: 205, weight: 20 },
  { id: 'yetibull', name: 'Frost Charger', hp: 60, damage: 18, speed: 2.6, radius: 0.8, xp: 7, gold: 6, ai: 'charger', color: 0xcfe2ff, scale: 1.5, minTime: 225, weight: 20 },
  { id: 'bogtroll', name: 'Sludge Titan', hp: 110, damage: 20, speed: 1.9, radius: 0.9, xp: 9, gold: 8, ai: 'chaser', color: 0x4a7a4a, scale: 1.7, minTime: 250, weight: 18 },
  // ---- behavior-diverse roster (#missed3): armored / buffer / flanker ----
  // ARMORED: a heavier phalanx that shrugs off frontal fire — flank it, burn it, or blow it up.
  { id: 'aegis', name: 'Aegis Phalanx', hp: 140, damage: 18, speed: 1.5, radius: 0.9, xp: 9, gold: 7, ai: 'armored', frontArmor: 0.72, color: 0x7f8fb0, scale: 1.7, minTime: 215, weight: 18 },
  // BUFFERS: stay behind the swarm and pulse an empower aura (faster + harder-hitting aliens).
  { id: 'warchanter', name: 'War-Chanter', hp: 48, damage: 8, speed: 1.7, radius: 0.6, xp: 8, gold: 7, ai: 'buffer', buff: { radius: 6.5, interval: 4 }, color: 0xe0a33a, scale: 1.05, minTime: 165, weight: 18 },
  { id: 'overseer', name: 'Hive Overseer', hp: 90, damage: 10, speed: 1.5, radius: 0.7, xp: 12, gold: 10, ai: 'buffer', buff: { radius: 8.5, interval: 3.4 }, color: 0xc04ad0, scale: 1.3, minTime: 235, weight: 14 },
  // FLANKERS: fast strafers that swing wide to the player's sides instead of charging head-on.
  { id: 'razorwing', name: 'Razorwing', hp: 14, damage: 9, speed: 4.4, radius: 0.42, xp: 3, gold: 2, ai: 'flanker', color: 0xff6a5a, scale: 0.8, minTime: 105, weight: 34 },
  { id: 'flankwraith', name: 'Flank Wraith', hp: 30, damage: 14, speed: 3.6, radius: 0.5, xp: 5, gold: 4, ai: 'flanker', color: 0x3fd0c0, scale: 1.0, minTime: 185, weight: 24 },
  // ---- v11 content-fill: eight more archetypes (one new AI: healer; rest reuse AI with fresh field combos) ----
  // HEALER (new AI): hangs back and mends wounded allies — burn it down first or the swarm never thins.
  { id: 'mender', name: 'Mender Drone', hp: 54, damage: 7, speed: 1.7, radius: 0.6, xp: 9, gold: 8, ai: 'healer', heal: { radius: 7, interval: 3.2, amount: 14 }, color: 0x5be08a, scale: 1.1, minTime: 200, weight: 16 },
  // KAMIKAZE: a fast flanker that detonates on death — don't let it reach you, but killing it still stings.
  { id: 'kamikaze', name: 'Razor Kamikaze', hp: 16, damage: 10, speed: 4.6, radius: 0.45, xp: 3, gold: 2, ai: 'flanker', explodeOnDeath: { radius: 2.6, damage: 20 }, color: 0xff7a3d, scale: 0.85, minTime: 150, weight: 28 },
  // SIEGE WALKER: a front-plated chaser — shrug off frontal fire; flank, crit or burn it.
  { id: 'siegewalker', name: 'Siege Walker', hp: 120, damage: 18, speed: 1.6, radius: 0.85, xp: 8, gold: 6, ai: 'chaser', frontArmor: 0.6, color: 0x8a96b0, scale: 1.6, minTime: 230, weight: 18 },
  // BROOD SPAWNER: a ranged caster that keeps coughing up swarmites — kill it to stop the bleed.
  { id: 'broodspawner', name: 'Brood Spawner', hp: 56, damage: 8, speed: 1.5, radius: 0.65, xp: 10, gold: 9, ai: 'ranged', rangedCooldown: 2.8, summon: { id: 'swarmling', count: 2, interval: 4.5 }, color: 0xc06ad0, scale: 1.2, minTime: 175, weight: 16 },
  // WAR TOTEM: a stationary-ish buffer with a huge empower aura — a priority kill in a pack.
  { id: 'wartotem', name: 'War Totem', hp: 70, damage: 6, speed: 1.2, radius: 0.7, xp: 11, gold: 9, ai: 'buffer', buff: { radius: 9, interval: 3.2 }, color: 0xe08a3a, scale: 1.25, minTime: 210, weight: 13 },
  // BOMB RUSHER: a charger that detonates on death — winds up, dashes in, and blows up if you pop it close.
  { id: 'bombrusher', name: 'Bomb Rusher', hp: 30, damage: 12, speed: 3.0, radius: 0.55, xp: 5, gold: 4, ai: 'charger', explodeOnDeath: { radius: 3, damage: 24 }, color: 0xd0502e, scale: 1.05, minTime: 160, weight: 24 },
  // HEX ORB: an orbiter that circles and harasses — annoying at range, light on its own.
  { id: 'hexorb', name: 'Hex Orb', hp: 24, damage: 11, speed: 3.0, radius: 0.48, xp: 4, gold: 3, ai: 'orbiter', color: 0x9b6cff, scale: 0.9, minTime: 155, weight: 26 },
  // TITAN OOZE: a tanky splitter that bursts into a pile of goolets — AoE it or get swarmed.
  { id: 'titanooze', name: 'Titan Ooze', hp: 90, damage: 12, speed: 1.6, radius: 0.85, xp: 7, gold: 5, ai: 'splitter', splitInto: 'blobling', splitCount: 5, color: 0x4ab090, scale: 1.6, minTime: 245, weight: 16 },
]

// Bosses are spawned by the director on a timer, not the random pool.
export const BOSS_LIST: EnemyDef[] = [
  {
    id: 'boss_golem',
    name: 'The Monolith Mauler',
    hp: 1400,
    damage: 24,
    speed: 1.7,
    radius: 1.8,
    xp: 120,
    gold: 80,
    ai: 'boss',
    color: 0x9aa3b0,
    scale: 3.2,
    patterns: ['slam', 'ring', 'slam'],
    enrageAt: 0.4,
    bossPower: 'quake', // erupts a ring of telegraphed ground hazards — area denial that forces movement
  },
  {
    id: 'boss_hydra',
    name: 'The Tendril Terror',
    hp: 3200,
    damage: 30,
    speed: 2.0,
    radius: 2.0,
    xp: 220,
    gold: 140,
    ai: 'boss',
    color: 0x6ad06a,
    scale: 3.4,
    splitInto: 'reaver',
    splitCount: 4,
    patterns: ['summon', 'beam', 'slam'],
    enrageAt: 0.4,
  },
  {
    id: 'boss_lich',
    name: 'Overmind Zerox',
    hp: 6000,
    damage: 38,
    speed: 2.2,
    radius: 2.1,
    xp: 400,
    gold: 260,
    ai: 'boss',
    color: 0xc8b0ff,
    scale: 3.6,
    patterns: ['beam', 'ring', 'slam'],
    enrageAt: 0.45,
    bossPower: 'weaponlock',
  },
  {
    id: 'boss_queen',
    name: 'The Brood Empress',
    hp: 9000,
    damage: 34,
    speed: 1.9,
    radius: 2.0,
    xp: 550,
    gold: 340,
    ai: 'boss',
    color: 0xd060a0,
    scale: 3.5,
    summon: { id: 'swarmling', count: 5, interval: 3.5 },
    patterns: ['ring', 'slam'], // summon via the summon-def timer only (was double-summoning through both paths)
    enrageAt: 0.4,
  },
  {
    id: 'boss_warlord',
    name: 'Mecha Tyranny X',
    hp: 14000,
    damage: 46,
    speed: 2.5,
    radius: 2.2,
    xp: 800,
    gold: 500,
    ai: 'boss',
    color: 0xcc5533,
    scale: 3.9,
    patterns: ['charge', 'slam', 'beam', 'slam'],
    enrageAt: 0.45,
    bossPower: 'clone',
  },
  { id: 'boss_rootzilla', name: 'Rootzilla', hp: 5000, damage: 30, speed: 1.8, radius: 2.0, xp: 300, gold: 200, ai: 'boss', color: 0x3f9a4e, scale: 3.4, summon: { id: 'locust', count: 5, interval: 3.5 }, patterns: ['slam', 'ring'], enrageAt: 0.4 },
  { id: 'boss_sandwyrm', name: 'Sandwyrm Supreme', hp: 6500, damage: 34, speed: 2.7, radius: 2.0, xp: 360, gold: 230, ai: 'boss', color: 0xc1502e, scale: 3.5, patterns: ['charge', 'slam', 'charge'], enrageAt: 0.45, bossPower: 'empower' },
  { id: 'boss_reaper', name: 'The Grim Streamer', hp: 7000, damage: 36, speed: 2.2, radius: 2.0, xp: 400, gold: 260, ai: 'boss', color: 0x5be0c8, scale: 3.5, patterns: ['beam', 'slam', 'ring'], enrageAt: 0.45, bossPower: 'drain' },
  { id: 'boss_forklift', name: 'Goblin Forklift Champion', hp: 4500, damage: 28, speed: 2.4, radius: 1.9, xp: 280, gold: 190, ai: 'boss', color: 0xffd23b, scale: 3.2, explodeOnDeath: { radius: 4, damage: 40 }, patterns: ['charge', 'slam'], enrageAt: 0.4 },
  { id: 'boss_scorpion', name: 'Mecha-Scorpion', hp: 8000, damage: 38, speed: 2.5, radius: 2.1, xp: 460, gold: 300, ai: 'boss', color: 0xc8923a, scale: 3.6, patterns: ['charge', 'beam', 'slam'], enrageAt: 0.4, bossPower: 'gravity' },
  { id: 'boss_fridge', name: 'The Fridge', hp: 9000, damage: 40, speed: 1.6, radius: 2.1, xp: 520, gold: 340, ai: 'boss', color: 0xc0c6d0, scale: 3.6, patterns: ['ring', 'slam', 'ring'], enrageAt: 0.4, bossPower: 'shield' },
  { id: 'boss_djskel', name: 'DJ Skeletron', hp: 7500, damage: 36, speed: 2.3, radius: 2.0, xp: 480, gold: 320, ai: 'boss', color: 0xb060d0, scale: 3.5, summon: { id: 'swarmling', count: 6, interval: 3 }, patterns: ['beam', 'ring'], enrageAt: 0.45 },
  // hidden/secret bosses — only summoned at high Boss Curse (not in any map's bossPool). Two tiers: Voidlord (curse ≥5),
  // then the apex Devourer (curse ≥8) — the true final test for curse-stacking players.
  { id: 'boss_voidlord', name: 'The Voidlord', hp: 22000, damage: 52, speed: 2.4, radius: 2.4, xp: 1200, gold: 800, ai: 'boss', color: 0x9b3cff, scale: 4.2, summon: { id: 'reaver', count: 3, interval: 4 }, patterns: ['ring', 'beam', 'charge', 'slam'], enrageAt: 0.5, bossPower: 'weaponlock', hidden: true },
  { id: 'boss_devourer', name: 'The Devourer of Worlds', hp: 36000, damage: 64, speed: 2.6, radius: 2.7, xp: 2000, gold: 1300, ai: 'boss', color: 0xff2e6a, scale: 4.6, summon: { id: 'revenant', count: 3, interval: 3.5 }, patterns: ['charge', 'ring', 'beam', 'slam', 'ring'], enrageAt: 0.55, bossPower: 'gravity', hidden: true },
]

export const ENEMIES: Record<string, EnemyDef> = {}
for (const e of [...ENEMY_LIST, ...BOSS_LIST]) ENEMIES[e.id] = e

/** Enemies eligible to spawn at a given run time, optionally restricted to a map's pool. */
export function eligibleEnemies(time: number, pool?: string[]): EnemyDef[] {
  return ENEMY_LIST.filter((e) => {
    if ((e.weight ?? 0) <= 0) return false
    if (time < (e.minTime ?? 0)) return false
    if (pool && pool.length > 0 && !pool.includes(e.id)) return false
    return true
  })
}
